import distinctColors from "distinct-colors"
import { GeoserverLayer } from "../state"

export type GeoserverCategoryLabel = { value: number; label: string; color?: string }

/** True when this layer uses labels from an attached bool/cat component. */
export function geoserverLayerHasAttachedComponentLabels(layer: GeoserverLayer): boolean {
  return layer.categoryLabels !== undefined
}

/** Categorical values and display labels for map legend and SLD styling. */
export function geoserverCategoricalEntries(layer: GeoserverLayer): GeoserverCategoryLabel[] {
  if (geoserverLayerHasAttachedComponentLabels(layer)) {
    return (layer.categoryLabels ?? []).filter(
      entry => entry.label.trim().length > 0 && entry.value !== undefined && entry.value !== null,
    )
  }

  return (layer.uniqueValues ?? []).map(value => ({ value, label: String(value) }))
}

export function geoserverCategoricalValues(layer: GeoserverLayer): number[] {
  return geoserverCategoricalEntries(layer).map(entry => entry.value)
}

export function geoserverCategoryDisplayLabel(
  layer: GeoserverLayer,
  value: number,
): string {
  const entry = geoserverCategoricalEntries(layer).find(e => e.value === value)
  return entry?.label ?? String(value)
}

export function hexToRgba(hex: string): [number, number, number, number] {
  const normalized = hex.trim()
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
    1,
  ]
}

function defaultDistinctRgba(count: number): Array<[number, number, number, number]> {
  return distinctColors({ count: Math.max(count, 1) }).map(color => {
    const rgba = color.rgba()
    return [rgba[0], rgba[1], rgba[2], 1] as [number, number, number, number]
  })
}

/** Default map colours from component category settings (when any colour is configured). */
export function geoserverValueColorsFromEntries(
  entries: GeoserverCategoryLabel[],
): Array<[number, number, number, number]> | undefined {
  if (entries.length === 0 || !entries.some(entry => entry.color)) {
    return undefined
  }

  const defaults = defaultDistinctRgba(entries.length)
  return entries.map((entry, index) => {
    if (entry.color) {
      return hexToRgba(entry.color)
    }
    return defaults[index] ?? defaults[0]
  })
}
